
import { StudentForm } from "@/components/students/StudentForm";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { ArrowLeft, SaveIcon } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useStudentLimit } from "@/hooks/useStudentLimit";
import { useState, useEffect, useRef } from "react";
import { useLanguagePath } from "@/utils/language";
import { useLanguage } from "@/components/LanguageContext";
import { useTranslations } from "@/hooks/useTranslations";

const NewStudent = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const studentData = location.state;
  const { currentCount } = useStudentLimit();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getPath } = useLanguagePath();
  const { language } = useLanguage();
  const { t } = useTranslations("students");
  const formRef = useRef<{ onSubmit: () => void } | null>(null);
  
  const handleSubmit = () => {
    setIsSubmitting(true);
    if (formRef.current) {
      formRef.current.onSubmit();
    }
  };
  
  const handleGoBack = () => {
    navigate(getPath("/dashboard/students"));
  };
  
  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 space-y-2 md:space-y-0">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold tracking-tight gradient-text">
            {t("newStudent.title")}
          </h2>
          <p className="text-muted-foreground dark:text-gray-300">
            {t("newStudent.subtitle")}
          </p>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href={getPath("/dashboard")} className="text-flame hover:text-flame/80">Dashboard</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href={getPath("/dashboard/students")} className="text-flame hover:text-flame/80">
                  {t("title")}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="font-medium dark:text-white">
                  {t("newStudent.title")}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            className="rounded-full shadow-sm hover:shadow-md transition-shadow dark:text-white dark:border-gray-700 dark:bg-gray-800"
            onClick={handleGoBack}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("actions.back")}
          </Button>
          <Button
            type="button"
            disabled={isSubmitting}
            onClick={handleSubmit}
            className="bg-flame text-white hover:bg-flame/90 rounded-md shadow-sm hover:shadow-md transition-all h-11 px-6"
          >
            {isSubmitting ? (
              <>
                <span className="mr-2 h-4 w-4 animate-spin">⟳</span>
                {t("newStudent.addButton")}
              </>
            ) : (
              <>
                <SaveIcon className="mr-2 h-4 w-4" />
                {t("newStudent.addButton")}
              </>
            )}
          </Button>
        </div>
      </div>
      
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-xl shadow-md border border-slate-100/40 dark:border-gray-700/40 p-4 md:p-6 hover:shadow-lg transition-all duration-300">
        <StudentForm ref={formRef} initialData={studentData} />
      </div>
    </div>
  );
};

export default NewStudent;
